import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';
import { submitContactForm } from '../api/apiClient';
import FAQ from '../components/FAQ';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Expandable Table States
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (sectionName) => {
    setOpenSection((prev) => (prev === sectionName ? null : sectionName));
  };

  useEffect(() => {
    // Initialize jQuery-based functionality safely
    if (window.jQuery) {
      const $ = window.jQuery;
      if ($.fn.tab) {
        $('.tab1').tab();
        $('.tab2').tab({ trigger_event_type: 'mouseover' });
      }
    }
    window.scrollTo(0, 0);
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be less than 255 characters';
    }

    if (formData.email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (formData.mobile_number) {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(formData.mobile_number.replace(/\D/g, ''))) {
        newErrors.mobile_number = 'Please enter a valid 10-digit mobile number';
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 2000) {
      newErrors.message = 'Message must be less than 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await submitContactForm(formData);
      if (response.status) {
        toast.success(response.message || 'Message sent successfully');
        setFormData({
          name: '',
          email: '',
          mobile_number: '',
          message: '',
        });
      } else {
        toast.error(response.message || 'Failed to send message');
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 text-gray-800 antialiased">
      <SEO
        title="Contact Us - Gateway Financial Services Ltd."
        description="Get in touch with Gateway Financial Services Limited. Our expert team is ready to assist you with personalized financial advisory, support, and investment queries."
      />

      {/* BEGIN: Hero Section */}
      <section
        className="pwu-hero-bg text-white position-relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(12, 59, 68, 0.7), rgba(12, 59, 68, 0.7)), url('/images/banners/contactus-banner.webp')",
        }}
      >
        <div className="container position-relative" style={{ zIndex: 10 }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="pwu-hero-title mb-4">Contact</h1>
            </div>
          </div>
        </div>
      </section>
      {/* END: Hero Section */}

      {/* BEGIN: Main Content */}
      {/* BEGIN: Main Content Wrapper */}
      <div style={{ backgroundColor: '#ebf3f5' }}>
        <main className="container py-5">
          {/* BEGIN: Get in Touch Form */}
          <section className="mb-5 position-relative cu-z-10">
            <h3 className="text-center mb-4 pb-2" data-aos="fade-up">
              Get in touch
            </h3>
            <div
              className="bg-white cu-rounded-xl cu-shadow-md overflow-hidden p-4 p-lg-5 border border-brand-light"
              style={{ transform: 'translateZ(0)' }}
            >
              <form
                onSubmit={handleSubmit}
                className="d-flex flex-column cu-gap-3 mx-auto"
                style={{ maxWidth: '800px' }}
              >
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`cu-input ${errors.name ? 'is-invalid' : ''}`}
                    placeholder="Name"
                  />
                  {errors.name && <div className="text-danger small mt-1">{errors.name}</div>}
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`cu-input ${errors.email ? 'is-invalid' : ''}`}
                    placeholder="Email"
                  />
                  {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                </div>
                <div>
                  <input
                    type="tel"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleChange}
                    className={`cu-input ${errors.mobile_number ? 'is-invalid' : ''}`}
                    placeholder="Phone no."
                  />
                  {errors.mobile_number && <div className="text-danger small mt-1">{errors.mobile_number}</div>}
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`cu-input ${errors.message ? 'is-invalid' : ''}`}
                    placeholder="Message"
                    rows="4"
                  ></textarea>
                  {errors.message && <div className="text-danger small mt-1">{errors.message}</div>}
                </div>
                <div className="mt-2 text-center">
                  <button type="submit" disabled={isSubmitting} className="cu-btn cu-shadow-md w-100 cu-w-lg-auto px-5">
                    {isSubmitting ? 'Sending...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </section>
          {/* END: Get in Touch Form */}

          {/* BEGIN: Let's Connect With Us & Map */}
          <section className="mb-5">
            <h3 className="text-center mb-4 pb-2" data-aos="fade-up">
              Let's Connect With Us
            </h3>

            <div
              className="bg-white cu-rounded-xl cu-shadow-sm overflow-hidden d-flex flex-column flex-lg-row position-relative cu-z-10 border border-brand-light"
              style={{ transform: 'translateZ(0)' }}
            >
              {/* Address Column */}
              <div className="connect-address-col cu-w-lg-50 p-4 p-lg-5 d-flex flex-column justify-content-center align-items-center bg-white order-2 order-lg-1">
                <div className="d-flex flex-column text-left cu-gap-4 w-100" style={{ maxWidth: '400px' }}>
                  <div className="d-flex align-items-start cu-gap-3">
                    <div className="mt-1 bg-gray-100 p-2 cu-rounded-full">
                      <svg
                        className="text-gray-600"
                        style={{ width: '20px', height: '20px' }}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </div>
                    <div>
                      <p className="font-weight-bold text-gray-900 h6 mb-1">Jasmine Tower, 31, Shakespeare Sarani</p>
                      <p className="text-gray-600 mb-0">2nd Floor, Kolkata : 700017</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-center cu-gap-3">
                    <div className="bg-gray-100 p-2 cu-rounded-full">
                      <svg
                        className="text-gray-600"
                        style={{ width: '20px', height: '20px' }}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                      </svg>
                    </div>
                    <p className="font-weight-bold text-gray-900 h6 mb-0">033- 2289 4461</p>
                  </div>

                  <div className="d-flex align-items-center cu-gap-3">
                    <div className="bg-gray-100 p-2 cu-rounded-full">
                      <svg
                        className="text-gray-600"
                        style={{ width: '20px', height: '20px' }}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                      </svg>
                    </div>
                    <p className="font-weight-bold text-gray-900 h6 mb-0">admin@gatewayfin.in</p>
                  </div>
                </div>

                <div
                  className="mt-5 pt-4 border-top border-brand-light w-100 text-center"
                  style={{ maxWidth: '400px' }}
                >
                  <p className="small font-weight-bold text-gray-500 text-uppercase mb-3">Get in Touch With US</p>
                  <div className="d-flex justify-content-center cu-gap-3">
                    <a href="#instagram" className="cu-icon-btn">
                      <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
                        <path
                          fillRule="evenodd"
                          d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </a>
                    <a href="#twitter" className="cu-icon-btn text-decoration-none">
                      <span className="font-weight-bold" style={{ fontSize: '1.2rem' }}>
                        X
                      </span>
                    </a>
                    <a href="#linkedin" className="cu-icon-btn">
                      <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
                        <path
                          fillRule="evenodd"
                          d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Map Column */}
              <div
                className="cu-w-lg-50 cu-map-container bg-gray-100 order-1 order-lg-2"
                style={{ overflow: 'hidden' }}
              >
                <iframe
                  title="Gateway Financial Services Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.9114662591965!2d88.3572094!3d22.544988999999994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a02771945eb53bf%3A0xfdcac3d0fbbec2e4!2sGATEWAY%20FINANCIAL%20SERVICES%20LTD!5e0!3m2!1sen!2sin!4v1771935316546!5m2!1sen!2sin"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </section>
          {/* END: Let's Connect With Us & Map */}

          {/* BEGIN: Expandable Tables Section */}
          <div className="accordion-wrapper mb-5">
            {/* Details Of Head Office */}
            <div className="accordion">
              <button
                className="accordion-label"
                style={{
                  borderTop: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                }}
                onClick={() => toggleSection('headOffice')}
                aria-expanded={openSection === 'headOffice'}
              >
                <span>Details Of Head Office</span>
                <span
                  className="accordion-icon"
                  aria-hidden="true"
                  style={{
                    transform: openSection === 'headOffice' ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.3s ease',
                    marginLeft: '10px',
                  }}
                >
                  &#10095;
                </span>
              </button>
              <div
                className="accordion-content"
                style={{
                  maxHeight: openSection === 'headOffice' ? "400px" : "0",
                  padding: openSection === 'headOffice' ? "5px 0px" : "0",
                  overflow: "hidden",
                  visibility: openSection === 'headOffice' ? "visible" : "hidden",
                  transition: "all 0.4s ease-in-out",
                }}
              >
                <div className="cu-table-container my-3">
                  <table className="cu-table mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '20%' }}>Stock Broker Name</th>
                        <th style={{ width: '10%' }}>Registration Number</th>
                        <th style={{ width: '20%' }}>Registered Address</th>
                        <th style={{ width: '20%' }}>Branch Address</th>
                        <th style={{ width: '14%' }}>Contact No</th>
                        <th style={{ width: '16%' }}>Email Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Gateway Financial Services Limited Limited</td>
                        <td>INZ000247937</td>
                        <td>
                          <i className="fa fa-map-marker" aria-hidden="true"></i> 31, Shakespeare Sarani, Jasmine Tower,
                          2nd Floor, Kolkata 700 017
                        </td>
                        <td>
                          <i className="fa fa-map-marker" aria-hidden="true"></i> 31, Shakespeare Sarani, Jasmine Tower,
                          2nd Floor, Kolkata 700 017
                        </td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9007088081
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> helpdesk-support@gatewayfin.in
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Escalation */}
            <div className="accordion">
              <button
                className="accordion-label"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                }}
                onClick={() => toggleSection('escalation')}
                aria-expanded={openSection === 'escalation'}
              >
                <span>Escalation</span>
                <span
                  className="accordion-icon"
                  aria-hidden="true"
                  style={{
                    transform: openSection === 'escalation' ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.3s ease',
                    marginLeft: '10px',
                  }}
                >
                  &#10095;
                </span>
              </button>
              <div
                className="accordion-content"
                style={{
                  maxHeight: openSection === 'escalation' ? "2800px" : "0",
                  padding: openSection === 'escalation' ? "5px 0px" : "0",
                  overflow: "hidden",
                  visibility: openSection === 'escalation' ? "visible" : "hidden",
                  transition: "all 0.6s ease-in-out",
                }}
              >
                <div className="cu-table-container my-3">
                  <table className="cu-table mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '13%' }}>Details</th>
                        <th style={{ width: '13%' }}>Contact Person</th>
                        <th style={{ width: '24%' }}>Contact Address</th>
                        <th style={{ width: '15%' }}>Contact No.</th>
                        <th style={{ width: '18%' }}>Email</th>
                        <th style={{ width: '17%' }}>Working Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <b>Level 1</b> -<br />
                          Customer Care
                        </td>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Amberleen Perrira
                        </td>
                        <td>
                          <i className="fa fa-map-marker" aria-hidden="true"></i> 31, Shakespeare Sarani, Jasmine Tower,
                          2nd Floor, Kolkata-17
                        </td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 6292288368
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> feedback@gatewayfin.in
                        </td>
                        <td>
                          <i className="fa fa-clock-o" aria-hidden="true"></i> 9:00am - 7:00pm
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Level 2</b> -<br />
                          Compliance Officer
                        </td>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Jayati Dasgupta
                        </td>
                        <td>
                          <i className="fa fa-map-marker" aria-hidden="true"></i> 31, Shakespeare Sarani, Jasmine Tower,
                          2nd Floor, Kolkata-17
                        </td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9836206380
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> compliance-legal@gatewayfin.in
                        </td>
                        <td>
                          <i className="fa fa-clock-o" aria-hidden="true"></i> 9:00am - 7:00pm
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Level 3</b> -<br />
                          Head Office Manager
                        </td>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Naveen Khemka
                        </td>
                        <td>
                          <i className="fa fa-map-marker" aria-hidden="true"></i> 31, Shakespeare Sarani, Jasmine Tower,
                          2nd Floor, Kolkata-17
                        </td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9830269483
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> operations@gatewayfin.in
                        </td>
                        <td>
                          <i className="fa fa-clock-o" aria-hidden="true"></i> 9:00am - 7:00pm
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Level 4</b> -<br />
                          CEO
                        </td>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Pratik Agarwal
                        </td>
                        <td>
                          <i className="fa fa-map-marker" aria-hidden="true"></i> 31, Shakespeare Sarani, Jasmine Tower,
                          2nd Floor, Kolkata-17
                        </td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9903629798
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> pratik@gatewayfin.in
                        </td>
                        <td>
                          <i className="fa fa-clock-o" aria-hidden="true"></i> 9:00am - 7:00pm
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* BEGIN: Level Cards */}
                <div className="cu-grid cu-grid-2 cu-grid-4 mb-4 mt-5">
                  {/* Level 1 */}
                  <div className="cu-level-card">
                    <div className="cu-level-top">
                      <h3 className="h5 font-weight-bold text-gray-900 mb-2">Level 1:</h3>
                      <p className="text-gray-600 mb-2" style={{ fontSize: '0.875rem' }}>
                        Trading &amp; Demat Account related queries:
                      </p>
                    </div>
                    <div className="cu-level-inner d-flex flex-column cu-gap-2" style={{ fontSize: '0.875rem' }}>
                      <div style={{ height: '24px' }}>
                        <p className="font-weight-bold text-gray-900 mb-0" style={{ fontSize: '0.75rem' }}>
                          Help:
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          helpdesk-support@gatewayfin.in
                        </p>
                      </div>
                      <div style={{ height: '24px' }}></div>
                      <div style={{ height: '40px' }}></div>
                      <div style={{ height: '24px' }}></div>
                      <div style={{ height: '40px' }}>
                        <p className="text-gray-500 mb-0" style={{ fontSize: '0.75rem' }}>
                          Monday—Friday from 9:00am to 7:00pm
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Level 2 */}
                  <div className="cu-level-card">
                    <div className="cu-level-top">
                      <h3 className="h5 font-weight-bold text-gray-900 mb-2">Level 2:</h3>
                      <p className="text-gray-600 mb-2" style={{ fontSize: '0.875rem' }}>
                        If you do not receive a satisfactory response at Level 1 within 3 working days, you may
                        contact our Compliance Officer at:
                      </p>
                    </div>
                    <div className="cu-level-inner d-flex flex-column cu-gap-2" style={{ fontSize: '0.875rem' }}>
                      <div style={{ height: '24px' }}>
                        <p className="font-weight-bold text-gray-900 mb-0" style={{ fontSize: '0.75rem' }}>
                          Jayati Dasgupta:
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          compliance-legal@gatewayfin.in
                        </p>
                      </div>
                      <div style={{ height: '24px' }}>
                        <p className="font-weight-bold text-gray-900 mb-0" style={{ fontSize: '0.75rem' }}>
                          DP related queries/complaints:
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          gatewaydp@gatewayfin.in
                        </p>
                      </div>
                      <div style={{ height: '24px' }}></div>
                      <div style={{ height: '40px' }}>
                        <p className="text-gray-500 mb-0" style={{ fontSize: '0.75rem' }}>
                          Monday—Friday from 9:00am to 7:00pm
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Level 3 */}
                  <div className="cu-level-card">
                    <div className="cu-level-top">
                      <h3 className="h5 font-weight-bold text-gray-900 mb-2">Level 3:</h3>
                      <p className="text-gray-600 mb-2" style={{ fontSize: '0.875rem' }}>
                        If you still have not received a satisfactory response at Level 2 within 3 working days, you may
                        contact our Head Office Manager at:
                      </p>
                    </div>
                    <div className="cu-level-inner d-flex flex-column cu-gap-2" style={{ fontSize: '0.875rem' }}>
                      <div style={{ height: '24px' }}>
                        <p className="font-weight-bold text-gray-900 mb-0" style={{ fontSize: '0.75rem' }}>
                          Naveen Khemka:
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          operations@gatewayfin.in
                        </p>
                      </div>
                      <div style={{ height: '24px' }}></div>
                      <div style={{ height: '40px' }}></div>
                      <div style={{ height: '24px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          9830269483
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="text-gray-500 mb-0" style={{ fontSize: '0.75rem' }}>
                          Monday—Friday from 9:00am to 7:00pm
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Level 4 */}
                  <div className="cu-level-card">
                    <div className="cu-level-top">
                      <h3 className="h5 font-weight-bold text-gray-900 mb-2">Level 4:</h3>
                      <p className="text-gray-600 mb-2" style={{ fontSize: '0.875rem' }}>
                        If you have not received a satisfactory response at Level 3 within 7 working days, you may also
                        approach our CEO at:
                      </p>
                    </div>
                    <div className="cu-level-inner d-flex flex-column cu-gap-2" style={{ fontSize: '0.875rem' }}>
                      <div style={{ height: '24px' }}>
                        <p className="font-weight-bold text-gray-900 mb-0" style={{ fontSize: '0.75rem' }}>
                          Mr. Pratik Agarwal
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          pratik@gatewayfin.in
                        </p>
                      </div>
                      <div style={{ height: '24px' }}></div>
                      <div style={{ height: '40px' }}></div>
                      <div style={{ height: '24px' }}>
                        <p className="d-flex align-items-center cu-gap-2 mb-0 text-gray-700">
                          <svg
                            className="text-gray-400"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            ></path>
                          </svg>
                          9903629798
                        </p>
                      </div>
                      <div style={{ height: '40px' }}>
                        <p className="text-gray-500 mb-0" style={{ fontSize: '0.75rem' }}>
                          Monday—Friday from 9:00am to 7:00pm
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* END: Level Cards */}
              </div>
            </div>

            {/* External Escalation */}
            <div className="accordion">
              <button
                className="accordion-label"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                }}
                onClick={() => toggleSection('external')}
                aria-expanded={openSection === 'external'}
              >
                <span>External Escalation</span>
                <span
                  className="accordion-icon"
                  aria-hidden="true"
                  style={{
                    transform: openSection === 'external' ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.3s ease',
                    marginLeft: '10px',
                  }}
                >
                  &#10095;
                </span>
              </button>
              <div
                className="accordion-content"
                style={{
                maxHeight: openSection === 'external' ? "800px" : "0",
                padding: openSection === 'external' ? "5px 0px" : "0",
                overflow: "hidden",
                visibility: openSection === 'external' ? "visible" : "hidden",
                transition: "all 0.5s ease-in-out",
              }}
              >
                <div className="cu-grid cu-grid-2 cu-grid-3 my-4 mx-2">
                  <a
                    href="https://scores.sebi.gov.in/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between p-3 cu-external-card cu-rounded-xl text-decoration-none"
                  >
                    <span className="font-weight-medium text-gray-900" style={{ fontSize: '0.875rem' }}>
                      Securities and Exchange
                      <br />
                      Board of India
                    </span>
                    <div className="cu-external-arrow cu-rounded-full p-2">
                      <svg
                        style={{ width: '16px', height: '16px' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://investorhelpline.nseindia.com/NICEPLUS/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between p-3 cu-external-card cu-rounded-xl text-decoration-none"
                  >
                    <span className="font-weight-medium text-gray-900" style={{ fontSize: '0.875rem' }}>
                      National Stock Exchange
                      <br />
                      of India Limited.
                    </span>
                    <div className="cu-external-arrow cu-rounded-full p-2">
                      <svg
                        style={{ width: '16px', height: '16px' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://bsecrs.bseindia.com/ecomplaint/frmInvestorHome.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between p-3 cu-external-card cu-rounded-xl text-decoration-none"
                  >
                    <span className="font-weight-medium text-gray-900" style={{ fontSize: '0.875rem' }}>
                      Bombay Stock Exchange.
                    </span>
                    <div className="cu-external-arrow cu-rounded-full p-2">
                      <svg
                        style={{ width: '16px', height: '16px' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://ncdex.com/investor-grievances/complaint-registration-form"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between p-3 cu-external-card cu-rounded-xl text-decoration-none"
                  >
                    <span className="font-weight-medium text-gray-900" style={{ fontSize: '0.875rem' }}>
                      National Commodity &amp;
                      <br />
                      Derivatives Exchange
                    </span>
                    <div className="cu-external-arrow cu-rounded-full p-2">
                      <svg
                        style={{ width: '16px', height: '16px' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://igrs.mcxindia.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between p-3 cu-external-card cu-rounded-xl text-decoration-none"
                  >
                    <span className="font-weight-medium text-gray-900" style={{ fontSize: '0.875rem' }}>
                      Multi Commodity
                      <br />
                      Exchange of India
                    </span>
                    <div className="cu-external-arrow cu-rounded-full p-2">
                      <svg
                        style={{ width: '16px', height: '16px' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://www.epass.nsdl.com/complaints/websitecomplaints.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between p-3 cu-external-card cu-rounded-xl text-decoration-none"
                  >
                    <span className="font-weight-medium text-gray-900" style={{ fontSize: '0.875rem' }}>
                      National Securities
                      <br />
                      Depository
                    </span>
                    <div className="cu-external-arrow cu-rounded-full p-2">
                      <svg
                        style={{ width: '16px', height: '16px' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* KMP Details */}
            <div className="accordion">
              <button
                className="accordion-label"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                }}
                onClick={() => toggleSection('kmp')}
                aria-expanded={openSection === 'kmp'}
              >
                <span>KMP Details</span>
                <span
                  className="accordion-icon"
                  aria-hidden="true"
                  style={{
                    transform: openSection === 'kmp' ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.3s ease',
                    marginLeft: '10px',
                  }}
                >
                  &#10095;
                </span>
              </button>
              <div
                className="accordion-content"
                style={{
                  maxHeight: openSection === 'kmp' ? "450px" : "0",
                  padding: openSection === 'kmp' ? "5px 0px" : "0",
                  overflow: "hidden",
                  visibility: openSection === 'kmp' ? "visible" : "hidden",
                  transition: "all 0.4s ease-in-out",
                }}
              >
                <div className="cu-table-container my-3">
                  <table className="cu-table mb-0">
                    <thead>
                      <tr>
                        <th>Name of KIMP</th>
                        <th>Designation</th>
                        <th style={{ minWidth: '120px' }}>Contact No</th>
                        <th>Email Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Pinky Agarwal
                        </td>
                        <td>Managing Director</td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9903989931
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> agarwalpinky1993@gmail.com
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Pratik Agarwal
                        </td>
                        <td>Executive Director</td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9903629798
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> pratik@gatewayfin.in
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Nishit Agarwal
                        </td>
                        <td>Executive Director</td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9674737336
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> nishit@gatewayfin.in
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <i className="fa fa-user" aria-hidden="true"></i> Jayati Dasgupta
                        </td>
                        <td>Compliance Officer</td>
                        <td>
                          <i className="fa fa-phone" aria-hidden="true"></i> 9836206380
                        </td>
                        <td>
                          <i className="fa fa-envelope" aria-hidden="true"></i> compliance-legal@gatewayfin.in
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* END: KMP Details */}
        </main>
      </div>
      {/* END: Main Content Wrapper */}

      <FAQ />

      {/* BEGIN: App Download Section */}
      <section id="app-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 align-self-center">
              <h3 data-aos="fade">Download our Investment Fund App</h3>
              <p>Investing With Gateway Financial Services Limited</p>
              <a href="#download" className="lm-btn">
                Download &amp; Explore More
              </a>
            </div>
            <div className="col-lg-6">
              <img
                src="/images/download.avif"
                className="img-fluid"
                alt="Download App Preview"
              />
            </div>
          </div>
        </div>
      </section>
      {/* END: App Download Section */}
    </div>
  );
};

export default ContactUs;
